import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefLabel } from "../../../../components/ref-label";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { PageHeader } from "../../../../components/breadcrumbs";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatTimestamp } from "../../../../lib/datetime";
import type { RefKind } from "../../../../lib/ref-names";
import {
  useAuditChainVerification,
  useAuditEntries,
  type AuditEntry,
} from "../hooks/audit-queries";
import { isAuditReadRefused, useAuditPermissions } from "../hooks/permissions";
import {
  AUDIT_ENTITY_TYPES,
  auditActionLabel,
  auditEntityLabel,
  auditEntityRefKind,
} from "../lib/audit-labels";
import { AUDIT_AREAS, auditSearchToQuery, type AuditArea, type AuditSearch } from "./search-params";
import "../audit.css";

// THE AUDIT TRAIL ON GLASS (spec 16 §2.16/§4).
//
// ONE SCREEN, TWO QUESTIONS, and it is the same screen because they are the
// same rows:
//   * unfiltered — "what happened here lately", the owner's view;
//   * with `?entity_type=&entity_id=` — "everything that happened to THIS
//     quarantine / product / work order", which is the question a factory
//     actually asks and the reason the URL carries the filters (a deep link IS
//     the History affordance an entity screen needs).
//
// THE INTEGRITY PANEL MAKES TWO SEPARATE CLAIMS AND KEEPS THEM SEPARATE.
// `chain` on the list response is about THE ROWS ON THIS PAGE — a filtered page
// is a sample, most of whose rows have no neighbour to link to — while
// `/v1/audit/chain/verify` is about a contiguous range from the beginning. They
// are rendered as two sentences under one heading rather than as one verdict,
// because folding them would let "this page checks out" be read as "your
// history is intact", which is exactly the claim this surface must not make
// loosely.
//
// A REDACTED PAYLOAD IS NOT AN EMPTY ONE. The API distinguishes absent-because-
// forbidden (`payload_redacted`, with the codes that would unlock it) from an
// empty object a writer really stored, and so does this: two different
// sentences, never one grey dash for both.
//
// TWO REFUSALS, TWO SENTENCES. `PERMISSION_DENIED` means "not your screen".
// `AUDIT_GROUP_SCOPE_REQUIRED` means "you hold the permission and your access is
// scoped to one site" — a facility_admin told to go find a code they already
// have would chase it for an afternoon.

const PAGE_LIMIT = 50;

function ActorCell({ row }: { row: AuditEntry }) {
  const { t } = useTranslation();
  if (row.actor_kind === "user") {
    // The person's name from the names read (UP6, spec 16 §3.13); a reader
    // who may not see names gets the short-id chip instead. The link filters
    // the trail to this actor, whose full id the filter needs.
    return (
      <Link
        to="/audit"
        search={{ actor: row.actor } as never}
        className="ds-lnk"
        data-testid="audit-actor-link"
      >
        <RefLabel kind="user" id={row.actor_user_id ?? row.actor} />
      </Link>
    );
  }
  if (row.actor_kind === "platform_staff") {
    return <span>{t("audit.actorKind.platform_staff")}</span>;
  }
  if (row.actor_kind === "system") return <span>{t("audit.actorKind.system")}</span>;
  if (row.actor_kind === "anonymous") return <span>{t("audit.actorKind.anonymous")}</span>;
  // An actor grammar this build does not know: show the RAW column, LABELLED as
  // unrecognised. The raw string is what the row was hashed with, so it is what
  // a dispute would quote — and the label stops a reader treating an
  // unparseable actor as a normal one.
  return (
    <span className="num" title={t("audit.actorKind.unknown")}>
      {row.actor}
    </span>
  );
}

/** Why a row's details are withheld, as one key: "needs" when some access
 * would unlock them, or "unmapped" when no rule names any. Rows sharing a key
 * share one sentence above the table instead of repeating it in every row
 * (rev-7). The permission codes stay on the cell's title for support; the
 * sentence a manager reads names no code. */
function redactionKey(row: AuditEntry): "needs" | "unmapped" {
  return row.payload_requires !== undefined && row.payload_requires.length > 0
    ? "needs"
    : "unmapped";
}

function PayloadCell({ row, repeated }: { row: AuditEntry; repeated: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (row.payload_redacted && repeated) {
    return (
      <span className="audit-redacted" data-testid="audit-payload-redacted">
        {t("audit.row.payloadHidden")}
      </span>
    );
  }

  if (row.payload_redacted) {
    // Absence + a sentence. Never a masked value, never a dash — a dash is what
    // this table renders for "there is no such thing", and this is not that.
    return (
      <span
        className="audit-redacted"
        data-testid="audit-payload-redacted"
        title={row.payload_requires?.join(", ")}
      >
        {t("audit.row.payloadRedacted")}
        {row.payload_requires !== undefined && row.payload_requires.length > 0 ? (
          <span className="audit-redacted-codes"> {t("audit.row.payloadRedactedRequires")}</span>
        ) : (
          <span className="audit-redacted-codes"> {t("audit.row.payloadUnmapped")}</span>
        )}
      </span>
    );
  }

  const payload = row.payload ?? {};
  if (Object.keys(payload).length === 0) {
    return <span className="audit-muted">{t("audit.row.payloadEmpty")}</span>;
  }

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        data-testid="audit-payload-toggle"
        onClick={() => {
          setOpen((prior) => !prior);
        }}
      >
        {open ? t("audit.row.hide") : t("audit.row.show")}
      </Button>
      {open ? (
        <pre className="audit-payload" data-testid="audit-payload">
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : null}
    </>
  );
}

export function AuditTrailScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search: AuditSearch = useSearch({ strict: false });
  const perms = useAuditPermissions();

  const query = useMemo(
    () => ({ ...auditSearchToQuery(search), limit: PAGE_LIMIT }),
    // The EMITTED params, not the search object: a semantically identical
    // filter set must not reset the page walk (the module-05 fold/reset race).
    [search],
  );
  const queryKey = JSON.stringify(query);

  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rowsAccum, setRowsAccum] = useState<AuditEntry[]>([]);

  useEffect(() => {
    setCursors([undefined]);
    setRowsAccum([]);
  }, [queryKey]);

  // A read this role is refused is not ISSUED — a 403 behind a skeleton, then
  // an error card whose Retry can never succeed, is the shape this avoids.
  const refused = isAuditReadRefused(perms);
  const cursor = cursors[cursors.length - 1];
  const page = useAuditEntries(query, cursor, !refused);
  // The chain check rides on the same two gates and is asked only once the
  // list has answered. A facility-scoped admin holds the code, and only the
  // server knows the scope (see hooks/permissions.ts), so the list's 403 is
  // the one refusal this screen cannot predict; the check behind it used to
  // draw a second.
  const verification = useAuditChainVerification(!refused && page.data !== undefined);

  useEffect(() => {
    if (page.data === undefined) return;
    setRowsAccum((prior) => foldPagedRows(prior, page.data.items));
  }, [page.data]);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  // The server's OWN second refusal — the one the client cannot predict,
  // because `/v1/auth/tenants` carries role codes and no scope.
  const scopeRefused =
    page.error instanceof RequestError && page.error.code === "AUDIT_GROUP_SCOPE_REQUIRED";
  const permissionRefused =
    page.error instanceof RequestError && page.error.code === "PERMISSION_DENIED";
  const serverRefused = scopeRefused || permissionRefused;

  // Rev-7: a withheld-details reason shared by two or more rows is said once,
  // above the table, and those rows' cells read a short "Not shown".
  const redactionCounts = new Map<string, number>();
  for (const row of rowsAccum) {
    if (!row.payload_redacted) continue;
    const key = redactionKey(row);
    redactionCounts.set(key, (redactionCounts.get(key) ?? 0) + 1);
  }
  const repeatedReasons = [...redactionCounts.entries()].filter(([, n]) => n > 1);

  const columns: DataTableColumn<AuditEntry>[] = [
    { key: "seq", header: t("audit.col.seq"), numeric: true, render: (row) => row.seq },
    {
      key: "when",
      header: t("audit.col.when"),
      age: true,
      render: (row) => formatTimestamp(row.occurred_at, i18n.language),
    },
    { key: "who", header: t("audit.col.who"), render: (row) => <ActorCell row={row} /> },
    {
      key: "action",
      header: t("audit.col.action"),
      id: true,
      // The code stays on the title: it is what the row was hashed with, and
      // what a support conversation quotes.
      render: (row) => <span title={row.action}>{auditActionLabel(i18n, row.action)}</span>,
    },
    {
      key: "entity",
      header: t("audit.col.entity"),
      render: (row) =>
        row.entity_type === null ? (
          <span className="audit-muted">{t("audit.row.noEntity")}</span>
        ) : (
          <span>
            <span title={row.entity_type}>{auditEntityLabel(i18n, row.entity_type)}</span>
            {row.entity_id === null ? null : (
              <>
                {" · "}
                {/* The record's own history: the entity-scoped view of this
                    screen, which needs the whole id the chip shortens. */}
                <Link
                  to="/audit"
                  search={{ entity_type: row.entity_type, entity_id: row.entity_id } as never}
                  className="ds-lnk"
                  data-testid="audit-entity-link"
                >
                  <RefLabel id={row.entity_id} {...entityKind(row.entity_type)} />
                </Link>
              </>
            )}
          </span>
        ),
    },
    {
      key: "payload",
      header: t("audit.col.payload"),
      render: (row) => (
        <PayloadCell row={row} repeated={(redactionCounts.get(redactionKey(row)) ?? 0) > 1} />
      ),
    },
  ];

  const setSearch = (next: Partial<AuditSearch>): void => {
    void navigate({ to: "/audit", search: { ...search, ...next } as never });
  };

  const entityScoped = search.entity_type !== undefined && search.entity_id !== undefined;
  // The record types by name, in the reader's alphabet. A type the link
  // carries that this build has no word for is still offered, as "Record".
  const entityTypeOptions = [
    ...AUDIT_ENTITY_TYPES,
    ...(search.entity_type !== undefined &&
    !(AUDIT_ENTITY_TYPES as readonly string[]).includes(search.entity_type)
      ? [search.entity_type]
      : []),
  ]
    .map((value) => ({ value, label: auditEntityLabel(i18n, value) }))
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  const pageChain = page.data?.chain;
  const verdict = verification.data;

  return (
    <section className="audit-screen" data-testid="audit-screen">
      <PageHeader
        title={entityScoped ? t("audit.entityHeading") : t("audit.title")}
        data-testid="audit-header"
      />
      <p className="audit-subtitle">{t("audit.subtitle")}</p>

      {/* The entity-scoped view is a dead end without a way back out to the
          whole trail — a reader who followed "Full audit trail" from a product
          has no other route to the unfiltered list. */}
      {entityScoped ? (
        <p className="audit-entity-link-row">
          {/* A LINK, because it navigates — links are never buttons
              (design-system §4 check 3b). */}
          <Link
            to="/audit"
            search={{} as never}
            className="audit-entity-link"
            data-testid="audit-back-to-all"
          >
            {t("audit.entityBackToAll")}
          </Link>
        </p>
      ) : null}

      <div className="filter-bar" role="group" aria-label={t("audit.filters.legend")}>
        <Field label={t("audit.filters.actionPrefix")}>
          <select
            data-testid="audit-filter-area"
            value={search.area ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSearch({ area: value === "" ? undefined : (value as AuditArea) });
            }}
          >
            <option value="">{t("audit.filters.actionPrefixAny")}</option>
            {AUDIT_AREAS.map((areaKey) => (
              <option key={areaKey} value={areaKey}>
                {areaLabel(t, areaKey)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t("audit.filters.entityType")}>
          <select
            data-testid="audit-filter-entity-type"
            value={search.entity_type ?? ""}
            onChange={(e) => {
              setSearch({ entity_type: e.target.value === "" ? undefined : e.target.value });
            }}
          >
            <option value="">{t("audit.filters.entityTypeAny")}</option>
            {entityTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t("audit.filters.entityId")}>
          <input
            data-testid="audit-filter-entity-id"
            value={search.entity_id ?? ""}
            placeholder={t("audit.filters.entityIdPlaceholder")}
            onChange={(e) => {
              setSearch({ entity_id: e.target.value === "" ? undefined : e.target.value });
            }}
          />
        </Field>

        <Field label={t("audit.filters.actor")}>
          <input
            data-testid="audit-filter-actor"
            value={search.actor ?? ""}
            placeholder={t("audit.filters.actorPlaceholder")}
            onChange={(e) => {
              setSearch({ actor: e.target.value === "" ? undefined : e.target.value });
            }}
          />
        </Field>

        <Field label={t("audit.filters.from")}>
          <input
            type="date"
            data-testid="audit-filter-from"
            value={search.from ?? ""}
            onChange={(e) => {
              setSearch({ from: e.target.value === "" ? undefined : e.target.value });
            }}
          />
        </Field>

        <Field label={t("audit.filters.to")}>
          <input
            type="date"
            data-testid="audit-filter-to"
            value={search.to ?? ""}
            onChange={(e) => {
              setSearch({ to: e.target.value === "" ? undefined : e.target.value });
            }}
          />
        </Field>

        <Button
          size="sm"
          variant="secondary"
          data-testid="audit-filter-clear"
          onClick={() => {
            void navigate({ to: "/audit", search: {} as never });
          }}
        >
          {t("audit.filters.clear")}
        </Button>
      </div>

      {/* The half-pair the API refuses, said before it is sent. */}
      {search.entity_id !== undefined && search.entity_type === undefined ? (
        <p className="audit-hint" data-testid="audit-entity-id-hint">
          {t("audit.filters.entityIdNeedsType")}
        </p>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("audit.refusedTitle")} body={t("audit.refusedBody")} />
      ) : null}

      {/* The scope refusal the client could not predict — its own sentence, so
          a facility_admin is not sent hunting for a code they already hold. */}
      {!refused && scopeRefused ? (
        <ForbiddenState title={t("audit.scopeRefusedTitle")} body={t("audit.scopeRefusedBody")} />
      ) : null}
      {!refused && permissionRefused ? (
        <ForbiddenState title={t("audit.refusedTitle")} body={t("audit.refusedBody")} />
      ) : null}

      {/* PAUSED, not slow: react-query holds a query while the browser reports
          no network, so a skeleton here would shimmer for as long as the cut
          lasts and promise nothing. */}
      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("audit.offlineTitle")} body={t("audit.offlineBody")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={6} cols={columns.length} label={t("audit.loading")} />
      ) : null}

      {!refused &&
      page.error !== null &&
      !serverRefused &&
      !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("audit.errorTitle")}
          detail={t("audit.errorBody")}
          retryLabel={t("audit.retry")}
          requestIdLabel={t("audit.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        // An entity-scoped empty is a DIFFERENT fact from "no filter matches",
        // and it is the one a reader most needs explained: the chain only holds
        // acts performed through the API, so a record that arrived by import or
        // by a data load genuinely has no history until someone changes it
        // here. Saying "widen the dates" there would send them looking for
        // rows that do not exist.
        <EmptyState
          title={entityScoped ? t("audit.emptyEntityTitle") : t("audit.emptyTitle")}
          body={entityScoped ? t("audit.emptyEntityBody") : t("audit.emptyBody")}
        />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          {repeatedReasons.length > 0 ? (
            <ul className="audit-redaction-notes" data-testid="audit-redaction-notes">
              {repeatedReasons.map(([key]) => (
                <li key={key}>
                  {key === "unmapped"
                    ? t("audit.row.payloadHiddenUnmapped")
                    : t("audit.row.payloadHiddenNeeds")}
                </li>
              ))}
            </ul>
          ) : null}
          <DataTable
            data-testid="audit-entries-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            rowTestId="audit-row"
            empty={null}
            caption={t("audit.title")}
          />

          {page.data?.next_cursor != null ? (
            <div className="audit-toolbar">
              <Button
                size="sm"
                data-testid="audit-load-more"
                onClick={() => {
                  const next = page.data?.next_cursor;
                  if (next != null) setCursors((prior) => [...prior, next]);
                }}
              >
                {t("audit.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {/* TWO CLAIMS, TWO SENTENCES — see the file header. */}
      {!refused && !serverRefused && (pageChain !== undefined || verdict !== undefined) ? (
        <Card title={t("audit.chain.heading")} data-testid="audit-chain-panel">
          {pageChain !== undefined ? (
            <p className="audit-chain-line" data-testid="audit-chain-page">
              {pageChain.rows_checked === 0
                ? t("audit.chain.pageEmpty")
                : pageChain.ok
                  ? t("audit.chain.pageOk", {
                      rows: pageChain.hashes_recomputed,
                      links: pageChain.links_checked,
                    })
                  : t("audit.chain.pageBroken", {
                      seq: pageChain.break_at_seq ?? "?",
                      reason: reasonText(t, pageChain.reason),
                    })}
            </p>
          ) : null}
          {pageChain !== undefined && pageChain.rows_checked > 0 ? (
            <>
              <p className="audit-chain-note">{t("audit.chain.pageScopeNote")}</p>
              {!pageChain.anchored_to_genesis ? (
                <p className="audit-chain-note">{t("audit.chain.notAnchored")}</p>
              ) : null}
            </>
          ) : null}

          <p className="audit-chain-heading">{t("audit.chain.verifyHeading")}</p>
          <p className="audit-chain-line" data-testid="audit-chain-whole">
            {verification.isPending
              ? t("audit.chain.verifyRunning")
              : verification.error !== null
                ? t("audit.chain.verifyError")
                : verdict === undefined || verdict.head_seq === 0
                  ? t("audit.chain.verifyEmpty")
                  : !verdict.ok
                    ? t("audit.chain.verifyBroken", {
                        seq: verdict.break_at_seq ?? "?",
                        reason: reasonText(t, verdict.reason),
                      })
                    : verdict.complete
                      ? t("audit.chain.verifyOk", {
                          length: verdict.length,
                          head: verdict.head_seq,
                        })
                      : t("audit.chain.verifyPartial", {
                          length: verdict.length,
                          to: verdict.to_seq,
                          head: verdict.head_seq,
                        })}
          </p>
        </Card>
      ) : null}
    </section>
  );
}

/** The names read answers this entity type's ids; others keep the chip. */
function entityKind(entityType: string): { kind?: RefKind } {
  const kind = auditEntityRefKind(entityType);
  return kind === undefined ? {} : { kind };
}

/**
 * Area code → its label, written out as nine LITERAL keys rather than a
 * template.
 *
 * `t(`audit.area.${key}`)` would work at runtime and be invisible to
 * `tools/ci/i18n-parity.ts`, whose AST walker can only resolve literal keys —
 * it counts computed ones as "skipped", so a missing area label would ship as a
 * raw dotted key in the filter dropdown with every gate green. Nine lines is
 * the price of the check.
 */
function areaLabel(t: (key: string) => string, area: AuditArea): string {
  switch (area) {
    case "identity":
      return t("audit.area.identity");
    case "catalogue":
      return t("audit.area.catalogue");
    case "onboarding":
      return t("audit.area.onboarding");
    case "inventory":
      return t("audit.area.inventory");
    case "production":
      return t("audit.area.production");
    case "quality":
      return t("audit.area.quality");
    case "waste":
      return t("audit.area.waste");
    case "files":
      return t("audit.area.files");
    case "agent":
      return t("audit.area.agent");
  }
}

/** The API's machine reason → a sentence. Unknown reasons say so. */
function reasonText(t: (key: string) => string, reason: string | undefined): string {
  if (reason === "seq_gap") return t("audit.chain.reason.seq_gap");
  if (reason === "prev_hash_mismatch") return t("audit.chain.reason.prev_hash_mismatch");
  if (reason === "curr_hash_mismatch") return t("audit.chain.reason.curr_hash_mismatch");
  return t("audit.chain.reason.unknown");
}
